import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Activity,
  Archive,
  CalendarRange,
  ClipboardPenLine,
  Download,
  FileSpreadsheet,
  KeyRound,
  MessageSquareText,
  Plus,
  Settings,
  Shield,
  Trash2,
  Upload,
  UserCog,
  Users,
} from "lucide-react";
import { api, ApiError, downloadApi, query } from "../api";
import type {
  ApiList,
  AppSettings,
  Balance,
  Delegation,
  FeedbackCategory,
  FeedbackItem,
  FeedbackPriority,
  FeedbackStatus,
  LeaveType,
  LeaveRequest,
  RequestStatus,
  Role,
  Team,
  User,
  ShiftChange,
} from "../types";
import {
  addIsoDays,
  dateLabel,
  dateRangeLabel,
  initials,
  LEAVE_LABELS,
  localDateTime,
  roleLabel,
  SHIFT_LABELS,
  STATUS_LABELS,
  todayIso,
} from "../utils";
import {
  Alert,
  Button,
  Empty,
  Field,
  Loading,
  Modal,
  PageHeader,
  SearchableCombobox,
  SelectField,
  StatusBadge,
  TextareaField,
} from "../components/ui";
import { HardDeleteAction } from "../components/HardDeleteAction";
import { parsePhoneImportFile, type PhoneImportRow } from "../personnel-phone-import";
import { PersonnelProfileModal } from "./PersonnelProfile";
import { feedbackCategoryLabels, feedbackStatusLabels } from "../feedback-meta";
import { ORGANIZATION_UNIT_LABELS, type OrganizationUnit } from "../../shared/domain-types";

type Tab =
  | "personnel"
  | "delegations"
  | "export"
  | "feedback"
  | "corrections"
  | "settings"
  | "audit";
interface ManagedUser extends User {
  balances?: Balance;
  annualBalance?: number | null;
  excuseBalance?: number | null;
}
interface PhoneImportPreview {
  items: Array<PhoneImportRow & { currentPhone: string | null; displayName: string | null; userId: string | null; status: "MATCHED" | "UNCHANGED" | "NOT_FOUND" | "DUPLICATE" }>;
  summary: { matched: number; unchanged: number; notFound: number; duplicate: number };
}
interface AuditItem {
  id: string | number;
  actorName?: string | null;
  action: string;
  entityType: string;
  entityId?: string | number | null;
  details?: string | Record<string, unknown> | null;
  createdAt: string;
}
interface RawAuditItem extends Partial<AuditItem> {
  id: string | number;
  action: string;
  actor_name?: string | null;
  entity_type?: string;
  entity_id?: string | number | null;
  summary?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
}
function items<T>(
  value: ApiList<T> | T[] | { personnel?: T[]; delegations?: T[]; logs?: T[] },
): T[] {
  if (Array.isArray(value)) return value;
  if ("items" in value) return value.items;
  return value.personnel ?? value.delegations ?? value.logs ?? [];
}
const teamOptions: Team[] = ["A", "B", "C", "D"];
const feedbackPriorityLabels: Record<FeedbackPriority, string> = { LOW: "Düşük", NORMAL: "Normal", HIGH: "Yüksek", URGENT: "Acil" };
const skillOptions = [
  { code: "QMSTR_L1", label: "QMSTR L1" },
  { code: "QMSTR_L2", label: "QMSTR L2" },
  { code: "CQM", label: "CQM" },
  { code: "B1", label: "B1" },
  { code: "TAP_TEST", label: "TAP TEST" },
] as const;
const manageableSkillCodes = new Set<string>(skillOptions.map((skill) => skill.code));
const skillLabel = (code: string) => skillOptions.find((skill) => skill.code === code)?.label ?? code;

function PersonnelForm({
  open,
  onClose,
  user,
  currentUser,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  user: ManagedUser | null;
  currentUser: User;
  onSaved: () => void;
}) {
  const fixedTeam =
    currentUser.role === "chief"
      ? (currentUser.mainTeam ?? currentUser.assignment?.mainTeam)
      : undefined;
  const restrictedManagementEditor = currentUser.role === "supervisor" || currentUser.role === "manager";
  const initialTeam =
    user?.mainTeam ?? user?.assignment?.mainTeam ?? fixedTeam ?? "A";
  const [loginCode, setLoginCode] = useState(user?.loginCode ?? "");
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [organizationUnit, setOrganizationUnit] = useState(user?.organizationUnit ?? currentUser.organizationUnit ?? "COMPOSITE");
  const [team, setTeam] = useState<Team>(initialTeam as Team);
  const [pattern, setPattern] = useState(
    user?.schedulePattern ??
      user?.assignment?.schedulePattern ??
      `${initialTeam}1`,
  );
  const [title, setTitle] = useState(user?.title ?? "TEKNİSYEN");
  const [role, setRole] = useState<Role>(user?.role ?? "employee");
  const [managementShift, setManagementShift] = useState<"R1" | "R2" | "DAY_08_17">(
    user?.managementShift ?? "R1",
  );
  const [selectedSkills, setSelectedSkills] = useState<string[]>(
    user?.skills?.filter((skill) => manageableSkillCodes.has(skill)) ?? [],
  );
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [hireDate, setHireDate] = useState(user?.hireDate ?? "");
  const [annual, setAnnual] = useState(
    (user?.annualBalance ?? user?.balances?.annual)?.toString() ?? "",
  );
  const [excuse, setExcuse] = useState(
    (user?.excuseBalance ?? user?.balances?.excuse)?.toString() ?? "",
  );
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [active, setActive] = useState(user?.active !== false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    const nextTeam =
      user?.mainTeam ?? user?.assignment?.mainTeam ?? fixedTeam ?? "A";
    setLoginCode(user?.loginCode ?? "");
    setDisplayName(user?.displayName ?? "");
    setOrganizationUnit(user?.organizationUnit ?? currentUser.organizationUnit ?? "COMPOSITE");
    setTeam(nextTeam as Team);
    setPattern(
      user?.schedulePattern ??
        user?.assignment?.schedulePattern ??
        `${nextTeam}1`,
    );
    setTitle(user?.title ?? "TEKNİSYEN");
    setRole(user?.role ?? "employee");
    setManagementShift(user?.managementShift ?? "R1");
    setSelectedSkills(user?.skills?.filter((skill) => manageableSkillCodes.has(skill)) ?? []);
    setPhone(user?.phone ?? "");
    setHireDate(user?.hireDate ?? "");
    setAnnual(
      (user?.annualBalance ?? user?.balances?.annual)?.toString() ?? "",
    );
    setExcuse(
      (user?.excuseBalance ?? user?.balances?.excuse)?.toString() ?? "",
    );
    setEffectiveDate(todayIso());
    setActive(user?.active !== false);
    setError("");
  }, [open, user?.id, fixedTeam]);
  useEffect(() => {
    if (role !== "supervisor" && role !== "manager" && !pattern.startsWith(team)) setPattern(`${team}1`);
  }, [team, pattern]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const managementRole = role === "supervisor" || role === "manager";
    if (!managementRole && !pattern.startsWith(team))
      return setError("Ana ekip ile alt düzen aynı ekip harfini taşımalıdır.");
    setLoading(true);
    const completeBody = {
      loginCode,
      displayName,
      organizationUnit: managementRole ? "R1_R2" : organizationUnit,
      mainTeam: managementRole ? null : team,
      schedulePattern: managementRole ? null : pattern,
      title,
      role: currentUser.role === "admin" ? role : "employee",
      managementShift: managementRole ? managementShift : null,
      phone: phone.trim() || null,
      ...(managementRole ? {} : {
        skills: selectedSkills,
        hireDate: hireDate || null,
        annualBalance: annual === "" ? null : Number(annual),
        excuseBalance: excuse === "" ? null : Number(excuse),
      }),
      effectiveDate,
      active,
    };
    const body = user
      ? currentUser.role === "chief"
        ? {
            loginCode,
            displayName,
            title,
            phone: phone.trim() || null,
            schedulePattern: pattern,
            effectiveDate,
          }
        : restrictedManagementEditor
          ? { displayName, title, phone: phone.trim() || null, active }
          : {
            loginCode,
            displayName,
            organizationUnit: managementRole ? "R1_R2" : organizationUnit,
            mainTeam: managementRole ? null : team,
            schedulePattern: managementRole ? null : pattern,
            title,
            role,
            managementShift: managementRole ? managementShift : null,
            phone: phone.trim() || null,
            ...(managementRole ? {} : {
              hireDate: hireDate || null,
              annualBalance: annual === "" ? null : Number(annual),
              excuseBalance: excuse === "" ? null : Number(excuse),
            }),
            effectiveDate,
            active,
          }
      : completeBody;
    try {
      if (user) {
        await api(`/api/personnel/${user.id}`, { method: "PATCH", body });
        if (!managementRole) {
          await api(`/api/personnel/${user.id}/skills`, {
            method: "PATCH",
            body: { skills: selectedSkills },
          });
        }
      } else await api("/api/personnel", { method: "POST", body });
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Personel kaydedilemedi.",
      );
    } finally {
      setLoading(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={user ? "Personel kaydını düzenle" : "Yeni personel ekle"}
      wide
    >
      <form className="form-stack" onSubmit={submit}>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {!user ? (
          <Alert>
            Başlangıç parolası personelin sicil numarası olarak
            oluşturulacaktır. Personel ilk girişte yeni parola belirlemek
            zorundadır.
          </Alert>
        ) : null}
        <div className="form-grid">
          <Field
            id="personnel-login-code"
            label="Sicil / giriş kodu"
            value={loginCode}
            onChange={(event) => setLoginCode(event.target.value)}
            required
            disabled={Boolean(user) && !["admin", "chief"].includes(currentUser.role)}
            hint={user && ["admin", "chief"].includes(currentUser.role)
              ? "Kalıcı parola korunur; hesap geçici parola durumundaysa yeni sicil ilk parola olur ve açık oturumlar kapatılır."
              : undefined}
          />
          <Field
            label="Ad soyad"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            required
          />
          {currentUser.role === "admin" && role !== "supervisor" && role !== "manager" ? (
            <SelectField label="Birim" value={organizationUnit} onChange={(event) => setOrganizationUnit(event.target.value as "COMPOSITE" | "STRUCTURAL" | "PAINT" | "R1_R2")}>
              <option value="COMPOSITE">Kompozit</option>
              <option value="STRUCTURAL">Yapısal</option>
              <option value="PAINT">Boya</option>
            </SelectField>
          ) : null}
          {role !== "supervisor" && role !== "manager" ? <>
            <SelectField
              label="Ana ekip"
              value={team}
              onChange={(event) => setTeam(event.target.value as Team)}
              disabled={currentUser.role === "chief"}
            >
              {teamOptions.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </SelectField>
            <SelectField
              label="Alt vardiya düzeni"
              value={pattern}
              onChange={(event) => setPattern(event.target.value)}
            >
              {[1, 2, 3, 4].map((number) => (
                <option key={number} value={`${team}${number}`}>
                  {team}
                  {number}
                </option>
              ))}
            </SelectField>
          </> : null}
          <Field
            label="Ünvan"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            required
          />
          {currentUser.role === "admin" ? (
            <SelectField
              label="Rol"
              value={role}
              onChange={(event) => setRole(event.target.value as Role)}
            >
              <option value="employee">Personel</option>
              <option value="chief">Başteknisyen</option>
              <option value="supervisor">Şef</option>
              <option value="manager">Müdür</option>
            </SelectField>
          ) : null}
          {role === "supervisor" || role === "manager" ? (
            <SelectField
              label="Yönetim vardiyası"
              value={managementShift}
              onChange={(event) => setManagementShift(event.target.value as "R1" | "R2" | "DAY_08_17")}
              required
            >
              <option value="R1">R1</option>
              <option value="R2">R2</option>
              <option value="DAY_08_17">08:00–17:00</option>
            </SelectField>
          ) : null}
          <Field
            label="Telefon (isteğe bağlı)"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
          {(currentUser.role === "admin" || !user) && role !== "supervisor" && role !== "manager" ? (
            <>
              <Field
                label="İşe giriş tarihi"
                type="date"
                value={hireDate}
                onChange={(event) => setHireDate(event.target.value)}
              />
              <Field
                label="Başlangıç yıllık bakiyesi"
                type="number"
                step="0.25"
                value={annual}
                onChange={(event) => setAnnual(event.target.value)}
              />
              <Field
                label="Başlangıç mazeret bakiyesi"
                type="number"
                step="0.25"
                value={excuse}
                onChange={(event) => setExcuse(event.target.value)}
              />
            </>
          ) : null}
          {user && role !== "supervisor" && role !== "manager" ? (
            <Field
              label="Değişikliğin geçerli olduğu tarih"
              type="date"
              value={effectiveDate}
              onChange={(event) => setEffectiveDate(event.target.value)}
              required
            />
          ) : null}
        </div>
        {role !== "supervisor" && role !== "manager" ? <fieldset className="checkbox-fieldset">
          <legend>Operasyon yetkileri (Skill)</legend>
          {skillOptions.map((skill) => (
            <label key={skill.code}>
              <input
                type="checkbox"
                checked={selectedSkills.includes(skill.code)}
                onChange={(event) =>
                  setSelectedSkills((current) =>
                    event.target.checked
                      ? [...current, skill.code]
                      : current.filter((item) => item !== skill.code),
                  )
                }
              />
              {skill.label}
            </label>
          ))}
        </fieldset> : null}
        {user?.skills?.some((skill) => !manageableSkillCodes.has(skill)) ? (
          <Alert>
            Mevcut diğer skill kayıtları korunur: {user.skills
              .filter((skill) => !manageableSkillCodes.has(skill))
              .map(skillLabel)
              .join(", ")}
          </Alert>
        ) : null}
        {user && currentUser.role === "admin" ? (
          <label className="check-row">
            <input
              type="checkbox"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
            />
            <span>
              <strong>Hesap aktif</strong>
              <small>
                Pasif hesap giriş yapamaz; geçmiş kayıtları korunur.
              </small>
            </span>
          </label>
        ) : null}
        <Alert>
          {role === "supervisor" || role === "manager"
            ? "Şef ve müdür hesapları operasyon ekranlarına salt okunur erişir; ekip kadrosuna dahil edilmez."
            : currentUser.role === "chief"
              ? "Yalnızca kendi biriminiz ve ana ekibinizdeki personelin sicil, temel bilgi, alt vardiya ve operasyon yetkilerini düzenleyebilirsiniz."
              : "Geçerli tarihli ekip/düzen değişikliği geçmiş takvim kayıtlarını bozmaz."}
        </Alert>
        <div className="modal-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button type="submit" loading={loading}>
            Kaydet
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function PersonnelManagement({
  currentUser,
  notify,
}: {
  currentUser: User;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const [people, setPeople] = useState<ManagedUser[]>([]);
  const [profileId, setProfileId] = useState<string | number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<ManagedUser | null | undefined>(
    undefined,
  );
  const [team, setTeam] = useState<Team | "">(
    currentUser.role === "chief"
      ? (currentUser.mainTeam ?? currentUser.assignment?.mainTeam ?? "")
      : "",
  );
  const [phoneImportOpen, setPhoneImportOpen] = useState(false);
  const [phoneRows, setPhoneRows] = useState<PhoneImportRow[]>([]);
  const [phonePreview, setPhonePreview] = useState<PhoneImportPreview | null>(null);
  const [phoneImportLoading, setPhoneImportLoading] = useState(false);
  async function selectPhoneFile(file: File | null) {
    if (!file) return;
    setPhoneImportLoading(true);
    setPhonePreview(null);
    try {
      const rows = await parsePhoneImportFile(file);
      setPhoneRows(rows);
      setPhonePreview(await api<PhoneImportPreview>("/api/admin/personnel/phones/preview", { method: "POST", body: { rows } }));
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Telefon dosyası okunamadı.", "danger");
    } finally {
      setPhoneImportLoading(false);
    }
  }
  async function commitPhoneImport() {
    if (!phonePreview?.summary.matched) return;
    setPhoneImportLoading(true);
    try {
      const result = await api<{ updated: number }>("/api/admin/personnel/phones/commit", { method: "POST", body: { rows: phoneRows } });
      notify(`${result.updated} personelin telefonu rehbere aktarıldı.`);
      setPhoneImportOpen(false);
      setPhonePreview(null);
      setPhoneRows([]);
      await load();
    } catch (caught) {
      notify(caught instanceof ApiError ? caught.message : "Telefon aktarımı tamamlanamadı.", "danger");
    } finally {
      setPhoneImportLoading(false);
    }
  }
  async function load() {
    setLoading(true);
    try {
      const result: ManagedUser[] = [];
      let offset: number | null = 0;
      while (offset !== null) {
        const page: ApiList<ManagedUser> & { nextOffset?: number | null } = await api(
          `/api/personnel${query({ team, includeInactive: true, management: true, offset, limit: 200 })}`);
        result.push(...items(page));
        offset = page.nextOffset ?? null;
      }
      setPeople(result.filter((person) => {
        if (currentUser.role === "admin") return !team || (person.mainTeam ?? person.assignment?.mainTeam) === team;
        if (currentUser.role === "chief") return person.organizationUnit === currentUser.organizationUnit
          && (person.mainTeam ?? person.assignment?.mainTeam) === team;
        if (["supervisor", "manager"].includes(currentUser.role)) return true;
        return false;
      }));
      setError("");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Personel listesi yüklenemedi.",
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, [team]);
  async function reset(user: ManagedUser) {
    if (
      !window.confirm(
        `${user.displayName} için parola sicil numarasına sıfırlansın ve tüm oturumları kapatılsın mı?`,
      )
    )
      return;
    try {
      const result = await api<{ message: string }>(
        `/api/personnel/${user.id}/reset-password`,
        { method: "POST" },
      );
      notify(result.message, "success");
    } catch (caught) {
      notify(
        caught instanceof ApiError ? caught.message : "Parola sıfırlanamadı.",
        "danger",
      );
    }
  }
  async function removeAccount(user: ManagedUser) {
    if (!window.confirm(
      `${user.displayName} hesabı pasif duruma alınsın mı? Kullanıcı artık giriş yapamaz; geçmiş izin, vardiya ve audit kayıtları korunur.`,
    )) return;
    const confirmation = window.prompt(
      `İşlemi onaylamak için ${user.loginCode} sicil numarasını yazın.`,
      "",
    );
    if (confirmation === null) return;
    if (confirmation.trim() !== user.loginCode) {
      notify("Sicil numarası eşleşmediği için hesap silme işlemi yapılmadı.", "danger");
      return;
    }
    try {
      const result = await api<{ message: string }>(`/api/personnel/${user.id}`, {
        method: "DELETE",
      });
      notify(result.message, "success");
      await load();
    } catch (caught) {
      notify(
        caught instanceof ApiError ? caught.message : "Hesap silinemedi.",
        "danger",
      );
    }
  }
  return (
    <div className="personnel-management">
      {currentUser.role === "admin" ? (
        <div className="management-filter">
          <SelectField
            label="Ekip"
            value={team}
            onChange={(event) => setTeam(event.target.value as Team | "")}
          >
            <option value="">Tüm ekipler</option>
            {teamOptions.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </SelectField>
        </div>
      ) : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading ? (
        <Loading label="Personel yönetimi yükleniyor" />
      ) : (
        <>
          <div className="management-actions">
            <span>{people.length} hesap</span>
            <div className="button-row">
              {currentUser.role === "admin" ? <Button variant="secondary" onClick={() => setPhoneImportOpen(true)}><Upload size={17} />Telefon Excel'i aktar</Button> : null}
              {["admin", "chief"].includes(currentUser.role) ? <Button onClick={() => setEditing(null)}><Plus size={17} />Personel ekle</Button> : null}
            </div>
          </div>
          <div className="management-table-wrap">
            <table className="management-table">
              <thead>
                <tr>
                  <th>Personel</th>
                  <th>Ekip / Düzen</th>
                  <th>Rol</th>
                  <th>Skill</th>
                  <th>Durum</th>
                  <th>
                    <span className="sr-only">İşlemler</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {people.map((person) => {
                  const personTeam =
                    person.mainTeam ?? person.assignment?.mainTeam;
                  const chiefCanManage = currentUser.role === "admin"
                    || (currentUser.role === "chief" && person.role === "employee" && String(person.id) !== String(currentUser.id))
                    || (["supervisor", "manager"].includes(currentUser.role) && ["PAINT", "R1_R2"].includes(person.organizationUnit ?? ""));
                  return (
                    <tr key={person.id}>
                      <td data-label="Personel">
                        <div className="table-person">
                          <span className="avatar avatar--small">
                            {initials(person.displayName)}
                          </span>
                          <div>
                            <strong>{person.displayName}</strong>
                            <span>
                              {person.loginCode} · {person.title}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td data-label="Ekip / Düzen">
                        {personTeam
                          ? `${personTeam} / ${person.schedulePattern ?? person.assignment?.schedulePattern ?? "—"}`
                          : person.managementShift
                            ? `Yönetim / ${person.managementShift === "DAY_08_17" ? "08:00–17:00" : person.managementShift}`
                            : "—"}
                      </td>
                      <td data-label="Rol">{roleLabel(person.role)}</td>
                      <td data-label="Skill">
                        {person.skills?.length ? (
                          <div className="profile-badges">
                            {person.skills.map((skill) => (
                              <b key={skill}>{skillLabel(skill)}</b>
                            ))}
                          </div>
                        ) : "—"}
                      </td>
                      <td data-label="Durum">
                        <span
                          className={
                            person.active === false
                              ? "status status--rejected"
                              : "status status--approved"
                          }
                        >
                          {person.active === false ? "Pasif" : "Aktif"}
                        </span>
                      </td>
                      <td data-label="İşlemler">
                        <div className="table-actions">
                          {person.canViewProfile ? <Button variant="ghost" onClick={() => setProfileId(person.id)}>Profil detayları</Button> : null}
                          {chiefCanManage ? <Button
                            variant="ghost"
                            onClick={() => setEditing(person)}
                          >
                            <UserCog size={16} />
                            Düzenle
                          </Button> : null}
                          {chiefCanManage ? <Button
                            variant="ghost"
                            onClick={() => void reset(person)}
                          >
                            <KeyRound size={16} />
                            Parolayı sicil numarasına sıfırla
                          </Button> : null}
                          {currentUser.role === "admin"
                            && person.role !== "admin"
                            && String(person.id) !== String(currentUser.id)
                            && person.active !== false ? (
                            <Button
                              variant="danger"
                              onClick={() => void removeAccount(person)}
                            >
                              <Trash2 size={16} />
                              Hesabı sil
                            </Button>
                          ) : null}
                          {currentUser.role === "admin"
                            && String(person.id) !== String(currentUser.id) ? (
                            <HardDeleteAction
                              kind="users"
                              targetId={person.id}
                              targetName={person.displayName}
                              onDeleted={load}
                            />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <PersonnelProfileModal id={profileId} onClose={() => setProfileId(null)} />
      <PersonnelForm
        open={editing !== undefined}
        onClose={() => setEditing(undefined)}
        user={editing ?? null}
        currentUser={currentUser}
        onSaved={() => {
          notify(editing ? "Personel kaydı güncellendi." : "Personel eklendi.");
          void load();
        }}
      />
      <Modal open={phoneImportOpen} onClose={() => { if (!phoneImportLoading) setPhoneImportOpen(false); }} title="Telefonları rehbere aktar" wide>
        <div className="form-stack phone-import">
          <Alert>Excel veya CSV dosyasında <strong>Sicil</strong> ve <strong>Telefon</strong> başlıkları bulunmalıdır. Eşleştirme yalnız sicil numarasıyla yapılır; diğer personel bilgileri değiştirilmez.</Alert>
          <Field type="file" label="Excel / CSV dosyası" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => void selectPhoneFile(event.target.files?.[0] ?? null)} />
          {phoneImportLoading ? <Loading label="Telefon aktarımı hazırlanıyor" /> : null}
          {phonePreview ? <>
            <div className="phone-import__summary">
              <span><b>{phonePreview.summary.matched}</b> güncellenecek</span>
              <span><b>{phonePreview.summary.unchanged}</b> aynı</span>
              <span><b>{phonePreview.summary.notFound}</b> bulunamadı</span>
              <span><b>{phonePreview.summary.duplicate}</b> mükerrer</span>
            </div>
            {phonePreview.items.some((item) => item.status === "NOT_FOUND" || item.status === "DUPLICATE") ? <Alert tone="warning">
              Sorunlu siciller: {phonePreview.items.filter((item) => item.status === "NOT_FOUND" || item.status === "DUPLICATE").slice(0, 20).map((item) => item.loginCode).join(", ")}
            </Alert> : null}
          </> : null}
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setPhoneImportOpen(false)} disabled={phoneImportLoading}>Vazgeç</Button>
            <Button onClick={() => void commitPhoneImport()} loading={phoneImportLoading} disabled={!phonePreview?.summary.matched}>Onayla ve aktar</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function DelegationsManagement({
  currentUser,
  notify,
}: {
  currentUser: User;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const ownTeam =
    currentUser.mainTeam ?? currentUser.assignment?.mainTeam ?? "A";
  const [team, setTeam] = useState<Team>(ownTeam);
  const [delegations, setDelegations] = useState<Delegation[]>([]);
  const [people, setPeople] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [delegateUserId, setDelegateUserId] = useState("");
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const delegateOptions = useMemo(
    () => people
      .map((person) => ({
        value: String(person.id),
        label: `${person.displayName} · ${person.loginCode} · ${person.organizationUnit === "STRUCTURAL" ? "Yapısal" : "Kompozit"}`,
      }))
      .sort((left, right) => left.label.localeCompare(right.label, "tr-TR", { sensitivity: "base", numeric: true })),
    [people],
  );
  async function load() {
    setLoading(true);
    try {
      const [delegationData, personnelData] = await Promise.all([
        api<ApiList<Delegation> | Delegation[] | { delegations: Delegation[] }>(
          `/api/delegations${query({ team })}`,
        ),
        api<ApiList<User> | User[] | { personnel: User[] }>(
          `/api/personnel${query({
            team,
            active: true,
            unit: currentUser.role === "chief" ? currentUser.organizationUnit : undefined,
          })}`,
        ),
      ]);
      setDelegations(
        items(delegationData).filter(
          (delegation) => delegation.team === team,
        ),
      );
      setPeople(
        items(personnelData).filter(
          (person) => String(person.id) !== String(currentUser.id),
        ).filter(
          (person) =>
            person.active !== false &&
            person.role === "employee" &&
            (person.mainTeam ?? person.assignment?.mainTeam) === team &&
            (currentUser.role !== "chief" || person.organizationUnit === currentUser.organizationUnit),
        ),
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Vekâlet bilgileri yüklenemedi.",
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, [team]);
  useEffect(() => {
    setDelegateUserId("");
  }, [team]);
  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api("/api/delegations", {
        method: "POST",
        body: { team, delegateUserId, startDate, endDate },
      });
      notify("Vekâlet atandı.");
      setOpen(false);
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Vekâlet atanamadı.",
      );
    } finally {
      setSaving(false);
    }
  }
  async function cancel(id: string | number) {
    if (!window.confirm("Bu vekâlet iptal edilsin mi?")) return;
    try {
      await api(`/api/delegations/${id}`, { method: "DELETE" });
      notify("Vekâlet iptal edildi.");
      await load();
    } catch (caught) {
      notify(
        caught instanceof ApiError
          ? caught.message
          : "Vekâlet iptal edilemedi.",
        "danger",
      );
    }
  }
  return (
    <>
      {currentUser.role === "admin" ? (
        <div className="management-filter">
          <SelectField
            label="Vekâlet ekibi"
            value={team}
            onChange={(event) => setTeam(event.target.value as Team)}
          >
            {teamOptions.map((item) => (
              <option value={item} key={item}>
                {item} Ekibi
              </option>
            ))}
          </SelectField>
        </div>
      ) : null}
      {error && !open ? <Alert tone="danger">{error}</Alert> : null}
      <Alert>
        Başteknisyen kendi ekibinden bir personel seçerek doğrudan vekâlet atar;
        sistem yöneticisi işlemi zorunlu değildir. Başlangıç ve bitiş günleri
        dâhildir; yetki sonraki gün otomatik sona erer. Bu sürede onay bildirimlerinde
        vekil önceliklidir; vekilin onayladığı işlem başteknisyene bildirilir.
      </Alert>
      {loading ? (
        <Loading label="Vekâletler yükleniyor" />
      ) : (
        <>
          <div className="management-actions">
            <span>{team} ekibi vekâletleri</span>
            {currentUser.role === "chief" ? <Button onClick={() => {
              setDelegateUserId("");
              setError("");
              setOpen(true);
            }} disabled={!people.length}>
              <Plus size={17} />
              Vekâlet ata
            </Button> : null}
          </div>
          {delegations.length ? (
            <div className="delegation-list">
              {delegations.map((delegation) => (
                <article key={delegation.id}>
                  <span
                    className={`team-dot team-dot--${delegation.team.toLowerCase()}`}
                  >
                    {delegation.team}
                  </span>
                  <div>
                    <strong>{delegation.delegateName}</strong>
                    <span>
                      {dateRangeLabel(delegation.startDate, delegation.endDate)}
                    </span>
                  </div>
                  <span
                    className={
                      delegation.currentlyActive
                        ? "status status--approved"
                        : delegation.scheduled
                          ? "status status--pending"
                        : "status status--cancelled"
                    }
                  >
                    {!delegation.active
                      ? "İptal"
                      : delegation.expired
                        ? "Sona erdi"
                        : delegation.scheduled
                          ? "Planlı"
                          : "Aktif"}
                  </span>
                  {delegation.active && !delegation.expired ? (
                    <Button
                      variant="danger"
                      onClick={() => void cancel(delegation.id)}
                    >
                      Vekâleti İptal Et
                    </Button>
                  ) : null}
                </article>
              ))}
            </div>
          ) : (
            <Empty
              icon={<Shield />}
              title="Vekâlet yok"
              text="Bu ekip için oluşturulmuş vekâlet bulunmuyor."
            />
          )}
        </>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Vekâlet ata">
        <form className="form-stack" onSubmit={save}>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <Field label="Ekip" value={`${team} Ekibi`} disabled />
          <SearchableCombobox
            label="Vekâlet verilecek personel"
            options={delegateOptions}
            value={delegateUserId}
            onChange={(value) => setDelegateUserId(String(value))}
            placeholder="Ad veya sicil ile personel seçin"
            hint="Yalnızca aynı birim ve ekipteki aktif personeller listelenir."
            required
          />
          <Field
            label="Başlangıç tarihi"
            type="date"
            min={todayIso()}
            value={startDate}
            onChange={(event) => {
              const value = event.target.value;
              setStartDate(value);
              if (endDate < value) setEndDate(value);
            }}
            required
          />
          <Field
            label="Bitiş tarihi"
            type="date"
            min={startDate}
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            required
          />
          <div className="modal-actions">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setOpen(false)}
            >
              Vazgeç
            </Button>
            <Button type="submit" loading={saving}>
              Vekâlet ata
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

function ExportManagement({
  currentUser,
  notify,
}: {
  currentUser: User;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const [types, setTypes] = useState<string[]>(["personnel", "leaves"]);
  const [format, setFormat] = useState<"xlsx" | "csv">("xlsx");
  const [startDate, setStartDate] = useState(todayIso().slice(0, 4) + "-01-01");
  const [endDate, setEndDate] = useState(todayIso());
  const ownTeam =
    currentUser.mainTeam ?? currentUser.assignment?.mainTeam ?? "A";
  const [teams, setTeams] = useState<Team[]>(
    currentUser.role === "chief" ? [ownTeam] : [...teamOptions],
  );
  const [leaveType, setLeaveType] = useState<LeaveType | "">("");
  const [status, setStatus] = useState<RequestStatus | "">("");
  const [loading, setLoading] = useState(false);
  const management = currentUser.role === "supervisor" || currentUser.role === "manager";
  const canSelectScope = currentUser.role === "admin" || management;
  const [organizationUnit, setOrganizationUnit] = useState<OrganizationUnit>("COMPOSITE");
  const options = [
    { value: "personnel", label: "Personel listesi" },
    { value: "leaves", label: "İzin talepleri" },
    { value: "shiftChanges", label: "Vardiya değişiklikleri" },
    { value: "missions", label: "Görevler ve dönüş süreleri" },
    { value: "balances", label: "Bakiyeler" },
    { value: "audit", label: "Onay ve audit geçmişi" },
    { value: "handovers", label: "Tahmini iş devirleri" },
  ].filter((option) => !management || !["balances", "audit"].includes(option.value));
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!types.length) return notify("En az bir veri türü seçin.", "danger");
    if (!teams.length) return notify("En az bir ekip seçin.", "danger");
    setLoading(true);
    try {
      await downloadApi(
        "/api/exports",
        {
          types,
          format,
          startDate,
          endDate,
          teams,
          ...(canSelectScope ? { organizationUnit } : {}),
          leaveType: leaveType || null,
          status: status || null,
        },
        `kompozit-rapor.${format === "xlsx" ? "xlsx" : types.length > 1 ? "zip" : "csv"}`,
      );
      notify("Rapor hazırlandı.");
    } catch (caught) {
      notify(
        caught instanceof ApiError ? caught.message : "Rapor oluşturulamadı.",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  }
  return (
    <form className="export-form" onSubmit={submit}>
      <Alert>
        Rapor içeriği sunucuda yetkilendirilir. Özel açıklamalar yalnızca
        yetkili ekip kapsamındaysa dışa aktarılır.
      </Alert>
      <fieldset className="checkbox-fieldset export-types">
        <legend>Veri türleri</legend>
        {options.map((option) => (
          <label key={option.value}>
            <input
              type="checkbox"
              checked={types.includes(option.value)}
              onChange={(event) =>
                setTypes((current) =>
                  event.target.checked
                    ? [...current, option.value]
                    : current.filter((item) => item !== option.value),
                )
              }
            />
            <FileSpreadsheet />
            {option.label}
          </label>
        ))}
      </fieldset>
      <div className="form-grid">
        {canSelectScope ? (
          <SelectField id="export-unit" label="Birim" value={organizationUnit} onChange={(event) => setOrganizationUnit(event.target.value as OrganizationUnit)}>
            {(["COMPOSITE", "STRUCTURAL", "PAINT"] as const).map((unit) => (
              <option key={unit} value={unit}>{ORGANIZATION_UNIT_LABELS[unit]}</option>
            ))}
          </SelectField>
        ) : null}
        <SelectField
          id="export-format"
          label="Format"
          value={format}
          onChange={(event) => setFormat(event.target.value as typeof format)}
        >
          <option value="xlsx">Excel XLSX</option>
          <option value="csv">CSV {types.length > 1 ? "(ZIP)" : ""}</option>
        </SelectField>
        {currentUser.role === "chief" ? (
          <Field label="Ekip" value={`${ownTeam} Ekibi`} disabled />
        ) : null}
        <Field
          label="Başlangıç tarihi"
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
        />
        <Field
          label="Bitiş tarihi"
          type="date"
          min={startDate}
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
        />
        <SelectField
          label="İzin türü"
          value={leaveType}
          onChange={(event) =>
            setLeaveType(event.target.value as LeaveType | "")
          }
        >
          <option value="">Tüm türler</option>
          {(Object.keys(LEAVE_LABELS) as LeaveType[]).map((item) => (
            <option value={item} key={item}>
              {LEAVE_LABELS[item]}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Durum"
          value={status}
          onChange={(event) =>
            setStatus(event.target.value as RequestStatus | "")
          }
        >
          <option value="">Tüm durumlar</option>
          {(Object.keys(STATUS_LABELS) as RequestStatus[]).map((item) => (
            <option value={item} key={item}>
              {STATUS_LABELS[item]}
            </option>
          ))}
        </SelectField>
      </div>
      {canSelectScope ? (
        <fieldset className="checkbox-fieldset export-teams">
          <legend>Ekipler</legend>
          {teamOptions.map((item) => (
            <label key={item}>
              <input
                type="checkbox"
                checked={teams.includes(item)}
                onChange={(event) =>
                  setTeams((current) =>
                    event.target.checked
                      ? [...current, item]
                      : current.filter((team) => team !== item),
                  )
                }
              />
              {item} Ekibi
            </label>
          ))}
        </fieldset>
      ) : null}
      <Button type="submit" loading={loading}>
        <Download size={18} />
        Raporu indir
      </Button>
    </form>
  );
}

type CorrectionItem = {
  kind: "leave" | "shift-change";
  id: string | number;
  displayName: string;
  team: Team | null;
  date: string;
  title: string;
  status: RequestStatus;
};

interface CorrectionCalendarResponse {
  days: Array<{
    leaves?: LeaveRequest[];
    shiftChanges?: ShiftChange[];
  }>;
}

function CorrectionsManagement({
  notify,
}: {
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const [team, setTeam] = useState<Team>("A");
  const [startDate, setStartDate] = useState(addIsoDays(todayIso(), -30));
  const [endDate, setEndDate] = useState(todayIso());
  const [items, setItems] = useState<CorrectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<CorrectionItem | null>(null);
  const [targetStatus, setTargetStatus] = useState<
    "APPROVED" | "REJECTED" | "CANCELLED"
  >("APPROVED");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    if (endDate < startDate) {
      setError("Bitiş tarihi başlangıçtan önce olamaz.");
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await api<CorrectionCalendarResponse>(
        `/api/calendar${query({
          team,
          start: startDate,
          end: endDate,
          view: "list",
        })}`,
      );
      const unique = new Map<string, CorrectionItem>();
      for (const day of result.days) {
        for (const leave of day.leaves ?? [])
          unique.set(`leave-${leave.id}`, {
            kind: "leave",
            id: leave.id,
            displayName: leave.displayName,
            team: leave.team,
            date: leave.startDate,
            title: `${LEAVE_LABELS[leave.type]} · ${dateRangeLabel(leave.startDate, leave.endDate)}`,
            status: leave.status,
          });
        for (const shift of day.shiftChanges ?? [])
          unique.set(`shift-${shift.id}`, {
            kind: "shift-change",
            id: shift.id,
            displayName: shift.displayName,
            team: shift.team,
            date: shift.date,
            title: `${SHIFT_LABELS[shift.currentShift]} → ${SHIFT_LABELS[shift.requestedShift]} · ${dateLabel(shift.date)}`,
            status: shift.status,
          });
      }
      setItems(
        [...unique.values()].sort((a, b) => b.date.localeCompare(a.date)),
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Düzeltilebilecek kayıtlar yüklenemedi.",
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [team]);

  function openCorrection(item: CorrectionItem) {
    setSelected(item);
    setTargetStatus(item.status === "APPROVED" ? "REJECTED" : "APPROVED");
    setNote("");
  }

  async function correct(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    if (note.trim().length < 5) {
      setError("Admin düzeltmesi için en az 5 karakter açıklama girin.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const endpoint =
        selected.kind === "leave"
          ? `/api/admin/leaves/${selected.id}/correction`
          : `/api/admin/shift-changes/${selected.id}/correction`;
      await api(endpoint, {
        method: "POST",
        body: { status: targetStatus, note: note.trim() },
      });
      setSelected(null);
      notify("Admin düzeltmesi audit kaydıyla uygulandı.");
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Admin düzeltmesi uygulanamadı.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Alert tone="warning">
        Buradaki durum değişiklikleri “admin düzeltmesi” olarak kaydedilir;
        açıklama ve eski/yeni durum audit geçmişinde korunur.
      </Alert>
      <div className="form-grid correction-filters">
        <SelectField
          label="Ekip"
          value={team}
          onChange={(event) => setTeam(event.target.value as Team)}
        >
          {teamOptions.map((item) => (
            <option value={item} key={item}>
              {item} Ekibi
            </option>
          ))}
        </SelectField>
        <Field
          label="Başlangıç tarihi"
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
        />
        <Field
          label="Bitiş tarihi"
          type="date"
          min={startDate}
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
        />
        <Button type="button" variant="secondary" onClick={() => void load()}>
          Kayıtları getir
        </Button>
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading ? (
        <Loading label="Düzeltme kayıtları yükleniyor" />
      ) : items.length ? (
        <div className="request-list correction-list">
          {items.map((item) => {
            const closedLeave =
              item.kind === "leave" &&
              ["CANCELLATION_PENDING", "CHANGE_PENDING", "SUPERSEDED", "CANCELLED"].includes(
                item.status,
              );
            return (
              <article className="request-card" key={`${item.kind}-${item.id}`}>
                <header>
                  <span
                    className={`team-dot team-dot--${(item.team ?? "management").toLowerCase()}`}
                  >
                    {item.team ?? "R1/R2"}
                  </span>
                  <div>
                    <strong>{item.displayName}</strong>
                    <span>{item.title}</span>
                  </div>
                  <StatusBadge status={item.status} />
                </header>
                <footer>
                  {closedLeave ? (
                    <small>
                      Bağlı iptal/değişiklik akışı sessizce düzeltilemez.
                    </small>
                  ) : (
                    <Button variant="ghost" onClick={() => openCorrection(item)}>
                      <ClipboardPenLine size={17} />
                      Admin düzeltmesi
                    </Button>
                  )}
                  {item.kind === "leave" ? (
                    <HardDeleteAction
                      kind="leaves"
                      targetId={item.id}
                      targetName={`${item.displayName} · ${item.title}`}
                      onDeleted={load}
                    />
                  ) : null}
                </footer>
              </article>
            );
          })}
        </div>
      ) : (
        <Empty
          icon={<ClipboardPenLine />}
          title="Kayıt bulunamadı"
          text="Seçili ekip ve tarih aralığında düzeltilebilecek kayıt yok."
        />
      )}
      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title="Admin düzeltmesi"
      >
        {selected ? (
          <form className="form-stack" onSubmit={correct}>
            <p>
              <strong>{selected.displayName}</strong> · {selected.title}
            </p>
            <SelectField
              label="Yeni durum"
              value={targetStatus}
              onChange={(event) =>
                setTargetStatus(
                  event.target.value as typeof targetStatus,
                )
              }
            >
              <option value="APPROVED">Onaylandı</option>
              <option value="REJECTED">Reddedildi</option>
              <option value="CANCELLED">İptal edildi</option>
            </SelectField>
            <TextareaField
              label="Düzeltme açıklaması"
              value={note}
              minLength={5}
              maxLength={1000}
              onChange={(event) => setNote(event.target.value)}
              required
              hint="Bu açıklama audit geçmişine yazılır."
            />
            <div className="modal-actions">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setSelected(null)}
              >
                Vazgeç
              </Button>
              <Button type="submit" loading={saving}>
                Düzeltmeyi uygula
              </Button>
            </div>
          </form>
        ) : null}
      </Modal>
    </>
  );
}

function FeedbackManagement({
  notify,
  initialId,
}: {
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
  initialId?: string | null;
}) {
  const [reports, setReports] = useState<FeedbackItem[]>([]);
  const [status, setStatus] = useState<FeedbackStatus | "">("");
  const [category, setCategory] = useState<FeedbackCategory | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const [editStatus, setEditStatus] = useState<FeedbackStatus>("RECEIVED");
  const [editPriority, setEditPriority] = useState<FeedbackPriority>("NORMAL");
  const [adminNote, setAdminNote] = useState("");
  const [publicReply, setPublicReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    try {
      const result = await api<ApiList<FeedbackItem>>(
        `/api/admin/feedback${query({ status, category, dateFrom, dateTo })}`,
      );
      setReports(items(result));
      setError("");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Geri bildirimler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [status, category, dateFrom, dateTo]);

  useEffect(() => {
    if (!initialId) return;
    let active = true;
    setError("");
    void api<FeedbackItem>(`/api/admin/feedback/${encodeURIComponent(String(initialId))}`)
      .then((detail) => {
        if (!active) return;
        setSelected(detail); setEditStatus(detail.status); setEditPriority(detail.priority); setAdminNote(detail.adminNote ?? ""); setPublicReply("");
      })
      .catch((caught: unknown) => {
        if (!active) return;
        setSelected(null); setError(caught instanceof ApiError ? caught.message : "Geri bildirim ayrıntısı yüklenemedi.");
      });
    return () => { active = false; };
  }, [initialId]);

  async function open(report: FeedbackItem) {
    setSelected(report); setError("");
    try {
      const detail = await api<FeedbackItem>(`/api/admin/feedback/${encodeURIComponent(String(report.id))}`);
      setSelected(detail); setEditStatus(detail.status); setEditPriority(detail.priority); setAdminNote(detail.adminNote ?? ""); setPublicReply("");
    } catch (caught) { setSelected(null); setError(caught instanceof ApiError ? caught.message : "Geri bildirim ayrıntısı yüklenemedi."); }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setSaving(true);
    try {
      await api(`/api/admin/feedback/${encodeURIComponent(String(selected.id))}`, {
        method: "PATCH",
        body: { status: editStatus, priority: editPriority, adminNote: adminNote.trim() || null, publicReply: publicReply.trim() || null },
      });
      setSelected(null);
      notify("Geri bildirim güncellendi.");
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Geri bildirim güncellenemedi.");
    } finally {
      setSaving(false);
    }
  }

  const statusClass = (value: FeedbackStatus) =>
    value === "RESOLVED" ? "approved" : value === "CLOSED" ? "rejected" : ["IN_REVIEW", "PLANNED", "IN_PROGRESS", "WAITING_USER"].includes(value) ? "change_pending" : "pending";

  return (
    <>
      <div className="management-filter feedback-filters">
        <SelectField label="Durum" value={status} onChange={(event) => setStatus(event.target.value as FeedbackStatus | "")}>
          <option value="">Tüm durumlar</option>
          {(Object.keys(feedbackStatusLabels) as FeedbackStatus[]).map((value) => (
            <option value={value} key={value}>{feedbackStatusLabels[value]}</option>
          ))}
        </SelectField>
        <SelectField label="Kategori" value={category} onChange={(event) => setCategory(event.target.value as FeedbackCategory | "")}>
          <option value="">Tüm kategoriler</option>
          {(Object.keys(feedbackCategoryLabels) as FeedbackCategory[]).map((value) => (
            <option value={value} key={value}>{feedbackCategoryLabels[value]}</option>
          ))}
        </SelectField>
        <Field label="Başlangıç" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
        <Field label="Bitiş" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
      </div>
      {error && !selected ? <Alert tone="danger">{error}</Alert> : null}
      {loading ? (
        <Loading label="Geri bildirimler yükleniyor" />
      ) : reports.length ? (
        <div className="management-table-wrap">
          <table className="management-table">
            <thead><tr><th>Gönderen</th><th>Kategori</th><th>Tarih</th><th>Durum</th><th><span className="sr-only">İşlem</span></th></tr></thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td data-label="Gönderen"><strong>{report.user.displayName}</strong><br /><small>{report.user.loginCode} · {report.user.mainTeam ?? "—"} / {report.user.schedulePattern ?? "—"}</small></td>
                  <td data-label="Kategori">{feedbackCategoryLabels[report.category]}</td>
                  <td data-label="Tarih">{localDateTime(report.createdAt)}</td>
                  <td data-label="Durum"><span className={`status status--${statusClass(report.status)}`}>{feedbackStatusLabels[report.status]}</span></td>
                  <td data-label="İşlem"><Button variant="ghost" onClick={() => void open(report)}>Ayrıntı</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty icon={<MessageSquareText />} title="Geri bildirim yok" text="Seçili filtrelerde geri bildirim bulunamadı." />
      )}
      <Modal open={Boolean(selected)} onClose={() => setSelected(null)} title="Geri bildirim ayrıntısı" wide>
        {selected ? (
          <form className="form-stack" onSubmit={save}>
            {error ? <Alert tone="danger">{error}</Alert> : null}
            <div className="feedback-detail-grid">
              <div><small>Gönderen</small><strong>{selected.user.displayName}</strong><span>{selected.user.loginCode} · {selected.user.mainTeam ?? "—"} / {selected.user.schedulePattern ?? "—"}</span></div>
              <div><small>Kayıt / kategori / tarih</small><strong>{selected.referenceCode} · {feedbackCategoryLabels[selected.category]}</strong><span>{localDateTime(selected.createdAt)}</span></div>
            </div>
            <div className="feedback-copy"><strong>Açıklama</strong><p>{selected.description}</p></div>
            {selected.expectedOutcome ? <div className="feedback-copy"><strong>Beklenen sonuç</strong><p>{selected.expectedOutcome}</p></div> : null}
            {selected.attachments?.length ? <div className="feedback-copy feedback-attachment feedback-attachment-gallery"><strong>Fotoğraflar ({selected.attachments.length})</strong><div>{selected.attachments.map((attachment) => <a href={`/api/admin/feedback/${encodeURIComponent(String(selected.id))}/attachments/${encodeURIComponent(String(attachment.id))}`} target="_blank" rel="noreferrer" key={attachment.id}><img src={`/api/admin/feedback/${encodeURIComponent(String(selected.id))}/attachments/${encodeURIComponent(String(attachment.id))}`} alt={attachment.name ?? "Geri bildirim fotoğrafı"} loading="lazy" /></a>)}</div></div> : null}
            {selected.events?.length ? <div className="feedback-admin-timeline"><strong>Süreç geçmişi</strong>{selected.events.map((event) => <article key={event.id}><div><b>{event.actor?.displayName ?? "Sistem"}</b>{event.visibility === "INTERNAL" ? <em>İç not</em> : null}</div><span>{event.type === "STATUS_CHANGED" && event.toStatus ? feedbackStatusLabels[event.toStatus] : event.message ?? event.type}</span><small>{localDateTime(event.createdAt)}</small></article>)}</div> : null}
            <div className="feedback-meta">
              <span>Sayfa: {selected.route}</span>
              <span>Cihaz: {selected.device} · {selected.pwaMode ? "PWA" : "Tarayıcı"}</span>
              <span>Sürüm: {selected.appVersion}</span>
              <span className="feedback-browser">{selected.browser}</span>
            </div>
            <SelectField label="Durum" value={editStatus} onChange={(event) => setEditStatus(event.target.value as FeedbackStatus)}>
              {(Object.keys(feedbackStatusLabels) as FeedbackStatus[]).map((value) => (
                <option value={value} key={value}>{feedbackStatusLabels[value]}</option>
              ))}
            </SelectField>
            <SelectField label="Öncelik" value={editPriority} onChange={(event) => setEditPriority(event.target.value as FeedbackPriority)}>{(Object.keys(feedbackPriorityLabels) as FeedbackPriority[]).map((value) => <option value={value} key={value}>{feedbackPriorityLabels[value]}</option>)}</SelectField>
            <TextareaField label="Kullanıcıya yanıt" value={publicReply} maxLength={2000} onChange={(event) => setPublicReply(event.target.value)} hint="Bu mesaj kullanıcıya görünür ve bildirim gönderir." />
            <TextareaField label="Admin notu" value={adminNote} maxLength={2000} onChange={(event) => setAdminNote(event.target.value)} hint="Bu not yalnızca sistem yöneticileri tarafından görülür." />
            <div className="modal-actions">
              <Button type="button" variant="ghost" onClick={() => setSelected(null)}>Vazgeç</Button>
              <Button type="submit" loading={saving}>Kaydet</Button>
            </div>
          </form>
        ) : null}
      </Modal>
    </>
  );
}

function SettingsManagement({
  settings,
  refreshSettings,
  notify,
}: {
  settings: AppSettings;
  refreshSettings: () => Promise<void>;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const [appName, setAppName] = useState(settings.appName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api("/api/admin/settings", {
        method: "PATCH",
        body: { appName: appName.trim() },
      });
      await refreshSettings();
      notify("Uygulama adı güncellendi.");
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Ayar güncellenemedi.",
      );
    } finally {
      setLoading(false);
    }
  }
  return (
    <form className="settings-form form-stack" onSubmit={submit}>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <Field
        label="Uygulama adı"
        value={appName}
        maxLength={80}
        onChange={(event) => setAppName(event.target.value)}
        required
        hint="Üst menü, giriş ekranı ve rapor başlıklarında kullanılır."
      />
      <Alert>
        Kurulu PWA'nın ana ekran etiketi tarayıcı önbelleği nedeniyle daha sonra
        yenilenebilir.
      </Alert>
      <Button type="submit" loading={loading}>
        <Settings size={18} />
        Ayarı kaydet
      </Button>
    </form>
  );
}

function AuditManagement() {
  const [logs, setLogs] = useState<AuditItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    api<ApiList<RawAuditItem> | RawAuditItem[] | { logs: RawAuditItem[] }>(
      `/api/admin/audit${query({ limit: 200 })}`,
    )
      .then((value) =>
        setLogs(
          items(value).map((row) => ({
            id: row.id,
            actorName: row.actorName ?? row.actor_name ?? null,
            action: row.action,
            entityType: row.entityType ?? row.entity_type ?? "kayıt",
            entityId: row.entityId ?? row.entity_id ?? null,
            details: row.details ?? row.summary ?? row.metadata ?? null,
            createdAt: row.createdAt ?? row.created_at ?? new Date().toISOString(),
          })),
        ),
      )
      .catch((caught) =>
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Audit geçmişi yüklenemedi.",
        ),
      )
      .finally(() => setLoading(false));
  }, []);
  const filtered = useMemo(
    () =>
      logs.filter(
        (log) =>
          !search ||
          `${log.actorName ?? ""} ${log.action} ${log.entityType}`
            .toLocaleLowerCase("tr-TR")
            .includes(search.toLocaleLowerCase("tr-TR")),
      ),
    [logs, search],
  );
  return (
    <>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <label className="search-field">
        <Activity />
        <span className="sr-only">Audit kaydı ara</span>
        <input
          placeholder="İşlem, kişi veya kayıt türü ara"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      {loading ? (
        <Loading label="Audit geçmişi yükleniyor" />
      ) : filtered.length ? (
        <div className="audit-list">
          {filtered.map((log) => (
            <article key={log.id}>
              <span className="audit-icon">
                <Activity />
              </span>
              <div>
                <strong>{log.action}</strong>
                <span>
                  {log.actorName ?? "Sistem"} · {log.entityType}
                  {log.entityId ? ` #${log.entityId}` : ""}
                </span>
                {log.details ? (
                  <small>
                    {typeof log.details === "string"
                      ? log.details
                      : JSON.stringify(log.details)}
                  </small>
                ) : null}
              </div>
              <time>{localDateTime(log.createdAt)}</time>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={<Archive />}
          title="Audit kaydı yok"
          text="Seçili kapsamda işlem geçmişi bulunamadı."
        />
      )}
    </>
  );
}

export function ManagementScreen({
  currentUser,
  settings,
  refreshSettings,
  notify,
  initialTab,
  feedbackId,
}: {
  currentUser: User;
  settings: AppSettings;
  refreshSettings: () => Promise<void>;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
  initialTab?: Tab;
  feedbackId?: string | null;
}) {
  const [tab, setTab] = useState<Tab>(initialTab ?? (feedbackId && currentUser.role === "admin" ? "feedback" : "personnel"));
  const tabs: Array<{
    id: Tab;
    label: string;
    icon: typeof Users;
    adminOnly?: boolean;
  }> = [
    { id: "personnel", label: "Personel", icon: Users },
    { id: "delegations", label: "Vekâlet ata", icon: CalendarRange },
    { id: "export", label: "Dışa aktar", icon: Download },
    { id: "feedback", label: "Geri Bildirimler", icon: MessageSquareText, adminOnly: true },
    {
      id: "corrections",
      label: "Düzeltmeler",
      icon: ClipboardPenLine,
      adminOnly: true,
    },
    { id: "settings", label: "Ayarlar", icon: Settings, adminOnly: true },
    { id: "audit", label: "Audit", icon: Activity, adminOnly: true },
  ];
  const visibleTabs = tabs.filter((item) => {
    if (currentUser.role === "supervisor" || currentUser.role === "manager") return item.id === "personnel" || item.id === "export";
    return !item.adminOnly || currentUser.role === "admin";
  });
  return (
    <div className="page">
      <PageHeader
        eyebrow="YÖNETİM"
        title={
          currentUser.role === "admin"
            ? "Sistem yönetimi"
            : currentUser.role === "supervisor" || currentUser.role === "manager"
              ? "Personel ve raporlar"
            : `${currentUser.mainTeam ?? currentUser.assignment?.mainTeam} ekibi yönetimi`
        }
        description="Yetkiniz kapsamındaki ekip, vekâlet, rapor ve ayar işlemleri."
      />
      <div className="management-tabs" role="tablist" aria-label="Yönetim bölümleri">
        {visibleTabs.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                type="button"
                role="tab"
                id={`management-tab-${item.id}`}
                aria-controls="management-panel"
                aria-selected={tab === item.id}
                tabIndex={tab === item.id ? 0 : -1}
                className={tab === item.id ? "active" : ""}
                onClick={() => setTab(item.id)}
                onKeyDown={(event) => {
                  const direction = event.key === "ArrowRight" || event.key === "ArrowDown"
                    ? 1
                    : event.key === "ArrowLeft" || event.key === "ArrowUp"
                      ? -1
                      : 0;
                  const targetIndex = event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? visibleTabs.length - 1
                      : direction
                        ? (index + direction + visibleTabs.length) % visibleTabs.length
                        : -1;
                  if (targetIndex < 0) return;
                  event.preventDefault();
                  const next = visibleTabs[targetIndex];
                  if (!next) return;
                  setTab(next.id);
                  requestAnimationFrame(() => document.getElementById(`management-tab-${next.id}`)?.focus());
                }}
                key={item.id}
              >
                <Icon />
                {item.label}
              </button>
            );
          })}
      </div>
      <section
        className="management-panel"
        id="management-panel"
        role="tabpanel"
        aria-labelledby={`management-tab-${tab}`}
        tabIndex={0}
      >
        {tab === "personnel" ? (
          <PersonnelManagement currentUser={currentUser} notify={notify} />
        ) : tab === "delegations" ? (
          <DelegationsManagement currentUser={currentUser} notify={notify} />
        ) : tab === "export" ? (
          <ExportManagement currentUser={currentUser} notify={notify} />
        ) : tab === "feedback" && currentUser.role === "admin" ? (
          <FeedbackManagement notify={notify} initialId={feedbackId} />
        ) : tab === "corrections" && currentUser.role === "admin" ? (
          <CorrectionsManagement notify={notify} />
        ) : tab === "settings" && currentUser.role === "admin" ? (
          <SettingsManagement
            settings={settings}
            refreshSettings={refreshSettings}
            notify={notify}
          />
        ) : tab === "audit" && currentUser.role === "admin" ? (
          <AuditManagement />
        ) : null}
      </section>
    </div>
  );
}
